import { API_ENDPOINTS, httpClientInstance } from "@/api"
import type {
  AllocatedResource,
  AllocationGroup,
  AllocationLog,
  BulkAllocationRequest,
  BulkAllocationResponse,
  CloudAllocationCreateRequest,
  CloudIntegration,
  CloudIntegrationCreateRequest,
  CloudResource,
  ResourceSyncResponse,
} from "@/types"

export class CloudAllocationService {
  private formatErrorMessage(errorData: any): string {
    if (typeof errorData === "string") return errorData
    if (errorData?.detail) return errorData.detail
    if (typeof errorData === "object" && errorData !== null) {
      const errors: string[] = []
      for (const [field, messages] of Object.entries(errorData)) {
        if (Array.isArray(messages) && messages.length > 0) {
          const formattedField = field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
          errors.push(`${formattedField}: ${messages[0]}`)
        }
      }
      if (errors.length > 0) return errors.join("\n")
    }
    return "Operation failed. Please try again."
  }

  async listIntegrations(): Promise<CloudIntegration[]> {
    try {
      return await httpClientInstance.get<CloudIntegration[]>(API_ENDPOINTS.cloudAllocation.integrations.list)
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  async createIntegration(data: CloudIntegrationCreateRequest): Promise<CloudIntegration> {
    try {
      return await httpClientInstance.post<CloudIntegration>(API_ENDPOINTS.cloudAllocation.integrations.create, data)
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  async syncResources(integrationId: number): Promise<ResourceSyncResponse> {
    try {
      return await httpClientInstance.post<ResourceSyncResponse>(
        API_ENDPOINTS.cloudAllocation.integrations.syncResources(integrationId)
      )
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  async listResources(): Promise<CloudResource[]> {
    try {
      return await httpClientInstance.get<CloudResource[]>(API_ENDPOINTS.cloudAllocation.resources.list)
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  /** Fetches /v1/cloud-allocation/allocations/list/ and flattens the grouped response. */
  async listAllocations(): Promise<AllocatedResource[]> {
    try {
      const groups = await httpClientInstance.get<AllocationGroup[]>(API_ENDPOINTS.cloudAllocation.allocations.list)
      return groups.flatMap((group) =>
        group.resources.map((resource) => ({
          ...resource,
          department_id: group.target.id,
          department_name: group.target.name,
          integration_id: group.integration.id,
          integration_name: group.integration.name,
        }))
      )
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  async createAllocation(data: CloudAllocationCreateRequest): Promise<void> {
    try {
      await httpClientInstance.post<void>(API_ENDPOINTS.cloudAllocation.allocations.create, data)
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  async removeAllocation(allocationId: number): Promise<void> {
    try {
      await httpClientInstance.delete<void>(API_ENDPOINTS.cloudAllocation.allocations.delete(allocationId))
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  /** Allocate multiple resources to one or more departments/members at once. */
  async bulkAllocate(data: BulkAllocationRequest): Promise<BulkAllocationResponse> {
    try {
      return await httpClientInstance.post<BulkAllocationResponse>(
        API_ENDPOINTS.cloudAllocation.allocations.bulkAllocate,
        data
      )
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }

  /** Audit log of allocation/release events, optionally filtered by department/member. */
  async listLogs(params?: { department?: number; member?: number }): Promise<AllocationLog[]> {
    try {
      return await httpClientInstance.get<AllocationLog[]>(API_ENDPOINTS.cloudAllocation.logs.list, params)
    } catch (error: any) {
      throw new Error(this.formatErrorMessage(error.response?.data))
    }
  }
}

export const cloudAllocationService = new CloudAllocationService()
